// RoomCaptureScreen — per-space asset counter grid (buildspec 20-ui
// §2 / §3 / §5.4a / §5.5 / §5.7 / §6).
//
// Deep-link landing for `/capture/spaces/:spaceId`. Reads the
// SessionDetail from the local PowerSync mirror via `useSessionDetail`,
// groups items by System, dispatches counter / zero-confirm patches
// through the capture-client (which owns the §7.3.4 500 ms per-item
// coalesce), and exposes the §1.1a Admin Preview banner when an admin
// opens a foreign session.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { Check, Plus } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router'

import type {
  CaptureItemRow,
  SessionDetail,
  SpaceWithItems,
} from '@citycare/shared'

import { useAuth } from '../../app/auth-provider'
import { useSessionDetail } from '../../hooks/powersync/use-session-detail'
import { useRoomExclusions } from '../../hooks/use-room-exclusions'
import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { EmptyState } from '../../ui/empty-state'
import { Spinner } from '../../ui/spinner'

import { AddAssetDialog } from '../components/add-asset-dialog'
import { CaptureHero } from '../components/capture-hero'
import { CaptureLayout } from '../components/capture-layout'
import { CreateVariantForm } from '../components/create-variant-form'
import { RemoveAssetConfirmDialog } from '../components/remove-asset-confirm-dialog'

import {
  applyOptimisticPatch,
  groupBySystem,
  isCaptureItemRow,
  mergeAuthoritativeWithPending,
  upsertItemInSpaces,
  type PendingCreate,
} from './room-capture-reconcile'
import { dispatchFreshCapture } from './room-capture-eager-catchup'
import {
  dispatchAddAssetSelection,
  useRoomRemoval,
} from './room-capture-removal'
import { StickyBottomBar } from '../components/sticky-bottom-bar'
import { SystemGroup } from '../components/system-group'
import { createSelectedCaptureClient } from '../client/capture-client-factory'
import { useRegisteredCaptureClient } from '../client/use-registered-capture-client'
import type {
  CaptureClient,
  CaptureEvent,
} from '../client/capture-client.types'
import type { AssetRowPatch } from '../components/asset-row'

import {
  mergeNavSpaceIntoSession,
  readSessionIdFromLocationState,
  readSpaceFromLocationState,
} from './room-capture-nav-state'

export interface RoomCaptureScreenProps {
  // Test seam — defaults to the env-gated REST/PowerSync factory.
  createClient?: () => CaptureClient & { destroy?: () => void }
}

export function RoomCaptureScreen({
  createClient = createSelectedCaptureClient,
}: RoomCaptureScreenProps = {}): ReactElement {
  const { spaceId = '' } = useParams<{ spaceId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, status } = useAuth()
  const sessionIdFromNav = readSessionIdFromLocationState(location.state)
  const spaceFromNav = readSpaceFromLocationState(location.state)

  // Read SessionDetail from the local PowerSync mirror. Pass an empty
  // string when no nav state is present so the hook idles (no SQL
  // fired).
  const sessionDetailResult = useSessionDetail(sessionIdFromNav ?? '')

  // Local snapshot mirroring the hook's `detail`, kept here so the
  // optimistic write reconciliation (`client.subscribe(item-changed)`)
  // can update items without fighting useQuery on the next render. The
  // hook's snapshot is the source of truth on initial load; the local
  // copy diverges only while dispatches are in-flight.
  const [session, setSession] = useState<SessionDetail | null>(null)
  // Track which session the local mirror was hydrated against so a
  // navigation to a different session reseeds rather than diffs.
  const seededFromIdRef = useRef<string | null>(null)
  useEffect(() => {
    // `useSessionDetail` fans out four queries (session, spaces,
    // items, room_type_assets) and the session row often resolves
    // before the spaces/items/templates land. Without the
    // `isLoading` guard, we'd seed local state from the partial
    // snapshot — `spaces: []`, `siteItems: []` — and the same-session
    // id check below would then ignore the later, complete snapshot,
    // leaving the screen stuck on "Room not found" or an empty asset
    // list. Wait until every query has resolved before seeding.
    // (Codex P1 on PR 387.)
    if (sessionDetailResult.isLoading) return
    const next = sessionDetailResult.detail
    if (next === null) return
    // Reseed only when the underlying session id changes; on every
    // other useQuery snapshot update we leave the local mirror alone
    // so optimistic writes aren't clobbered by a re-render
    // mid-dispatch. Sync-stream-delivered updates after the initial
    // seed flow into the local mirror via the
    // `client.subscribe(item-changed)` reconciliation handler below.
    if (seededFromIdRef.current !== next.session.id) {
      seededFromIdRef.current = next.session.id
      // Merge the just-created-room nav space when the local mirror
      // hasn't replicated it yet — without this seed, optimistic
      // updates keyed on `spaceId` in `prev.spaces` silently drop.
      // Idempotent on the space id. (Codex P1 on PR 399.)
      setSession(mergeNavSpaceIntoSession(next, spaceFromNav))
    }
  }, [sessionDetailResult.detail, sessionDetailResult.isLoading, spaceFromNav])

  const loadError = sessionDetailResult.error?.message ?? null

  const [isAddAssetOpen, setAddAssetOpen] = useState(false)

  // In-flight createItem tracker; merge logic in room-capture-reconcile.ts.
  const pendingCreatesRef = useRef<Map<string, PendingCreate>>(new Map())

  // Room routes reuse this component across `spaceId` changes; scope the
  // client/event bus to the active room so delayed echoes cannot cross rooms.
  const createRoomClient = useCallback(() => {
    void spaceId
    return createClient()
  }, [createClient, spaceId])
  const client = useRegisteredCaptureClient(createRoomClient)

  // Reconcile authoritative events from the capture client. Merge +
  // upsert helpers live in ./room-capture-reconcile.ts.
  useEffect(() => {
    const unsubscribe = client.subscribe((event: CaptureEvent) => {
      if (event.kind !== 'item-changed' && event.kind !== 'item-reconciled') {
        return
      }
      if (!isCaptureItemRow(event.item)) return
      const authoritative: CaptureItemRow = event.item

      const pendingKey = `${spaceId}:${authoritative.assetTypeId}`
      const pending = pendingCreatesRef.current.get(pendingKey)
      pendingCreatesRef.current.delete(pendingKey)
      const { row: mergedRow, serverIsBehind } = mergeAuthoritativeWithPending(
        authoritative,
        pending,
      )
      if (serverIsBehind && pending !== undefined) {
        void client
          .dispatch({
            kind: 'updateItem',
            itemId: authoritative.id,
            quantity: pending.quantity,
            isZeroConfirmed: pending.isZeroConfirmed,
            notes: pending.notes,
            syncVersion: authoritative.syncVersion,
          })
          .catch(() => {
            // Drop-and-let-next-tap-retry policy.
          })
      }

      setSession((prev) =>
        prev === null
          ? prev
          : { ...prev, spaces: upsertItemInSpaces(prev.spaces, spaceId, mergedRow) },
      )
    })
    return unsubscribe
  }, [client, spaceId])

  const space: SpaceWithItems | null = useMemo(() => {
    if (session === null) return null
    const found = session.spaces.find((s) => s.id === spaceId)
    if (found !== undefined) return found
    // Fallback for the just-created-room race: the local PowerSync
    // mirror hasn't yet replicated the new space row, so it's missing
    // from session.spaces. The building screen forwards the
    // SpaceWithItems via navigation state precisely for this window.
    if (spaceFromNav !== null && spaceFromNav.id === spaceId) {
      return spaceFromNav
    }
    return null
  }, [session, spaceId, spaceFromNav])

  const handleUpdate = useCallback(
    (assetTypeId: string, patch: AssetRowPatch): void => {
      if (session === null || space === null) return
      // Lookup by assetTypeId — synthetic template rows share id ''
      // and would otherwise collapse into a single React node.
      const existing = space.items.find(
        (it) => it.assetTypeId === assetTypeId,
      )
      if (existing === undefined) return

      const nextQuantity = patch.quantity ?? existing.quantity
      const nextZeroConfirmed =
        patch.isZeroConfirmed ?? existing.isZeroConfirmed
      setSession((prev) => {
        if (prev === null) return prev
        return {
          ...prev,
          spaces: prev.spaces.map((s) =>
            s.id === spaceId
              ? {
                  ...s,
                  items: s.items.map((it) =>
                    it.assetTypeId === assetTypeId
                      ? applyOptimisticPatch(it, patch)
                      : it,
                  ),
                }
              : s,
          ),
        }
      })

      // Synthetic rows go through createItem on first write.
      const isSynthetic = existing.id === '' || existing.isTemplateOnly
      if (isSynthetic) {
        const pendingKey = `${spaceId}:${assetTypeId}`
        const priorPending = pendingCreatesRef.current.get(pendingKey)
        const alreadyPending = priorPending !== undefined
        const pendingEntry: PendingCreate = {
          quantity: nextQuantity,
          isZeroConfirmed: nextZeroConfirmed,
          notes: existing.notes,
          createResult: priorPending?.createResult,
        }
        pendingCreatesRef.current.set(pendingKey, pendingEntry)
        // First tap → createItem; later taps → durable eager catch-up so the
        // count survives a room-exit before the create echo (the "set 3, came
        // back to 1" race). See room-capture-eager-catchup.ts.
        dispatchFreshCapture(client, pendingCreatesRef.current, {
          session: session.session,
          spaceId,
          assetTypeId,
          pending: pendingEntry,
          pendingKey,
          alreadyPending,
        })
        return
      }

      void client
        .dispatch({
          kind: 'updateItem',
          itemId: existing.id,
          quantity: nextQuantity,
          isZeroConfirmed: nextZeroConfirmed,
          notes: existing.notes,
          syncVersion: existing.syncVersion,
        })
        .catch(() => {
          // Reconciler rolls optimistic state back via item-reconciled.
        })
    },
    [client, session, space, spaceId],
  )

  const {
    pendingConfirm,
    pendingRemoveAssetTypeIds,
    handleRequestRemove,
    handleConfirmRemove,
    handleCancelRemove,
    handleClearPendingRemove,
  } = useRoomRemoval(space, handleUpdate)

  const visibleItems = useMemo(
    () =>
      space === null
        ? []
        : space.items.filter(
            (i) =>
              !i.isZeroConfirmed &&
              !pendingRemoveAssetTypeIds.has(i.assetTypeId),
          ),
    [space, pendingRemoveAssetTypeIds],
  )
  const systemBuckets = useMemo(
    () => groupBySystem(visibleItems),
    [visibleItems],
  )
  const roomExclusions = useRoomExclusions(space?.roomTypeId ?? null)
  const excludedAssetTypeIds = useMemo(() => {
    const ids = new Set<string>(roomExclusions.ids)
    for (const it of visibleItems) ids.add(it.assetTypeId)
    return ids
  }, [visibleItems, roomExclusions.ids])

  const handleSelectAssetType = useCallback(
    async (assetTypeId: string): Promise<void> => {
      if (session === null || space === null) return
      await dispatchAddAssetSelection({
        client,
        sessionId: session.session.id,
        spaceId,
        items: space.items,
        assetTypeId,
      })
      // Codex P2 on PR #415: drop from the same-session removal guard.
      handleClearPendingRemove(assetTypeId)
    },
    [client, session, space, spaceId, handleClearPendingRemove],
  )

  const authPending = status === 'loading' || user === undefined
  const isAdminPreview =
    !authPending &&
    user !== undefined &&
    user.role === 'admin' &&
    session !== null &&
    session.session.userId !== user.userID
  const canEdit = !authPending && !isAdminPreview && sessionIdFromNav !== null

  const subtitleParts: string[] = []
  if (space !== null) {
    subtitleParts.push(space.roomTypeName)
    if (space.floorName !== null) subtitleParts.push(space.floorName)
  }
  const heroSubtitle =
    subtitleParts.length > 0 ? subtitleParts.join(' · ') : null
  const totalCount = visibleItems.length
  const doneCount = visibleItems.filter(
    (i) => parseFloat(i.quantity) > 0,
  ).length
  const heroCountLine =
    space !== null && totalCount > 0
      ? `${doneCount}/${totalCount} assets captured`
      : null

  const handleDone = (): void => {
    const buildingIdFromSession = session?.session.buildingId ?? null
    if (buildingIdFromSession !== null) {
      void navigate(
        `/capture/buildings/${encodeURIComponent(buildingIdFromSession)}`,
      )
      return
    }
    void navigate(-1)
  }
  return (
    <CaptureLayout>
      <section
        className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-safe"
        aria-label={
          space !== null ? `Capture ${space.displayName}` : 'Room capture'
        }
      >
        <h1 className="sr-only">Room capture</h1>

        {space !== null ? (
          <CaptureHero
            eyebrow="Room"
            name={space.displayName}
            subtitle={heroSubtitle}
            countLine={heroCountLine}
          />
        ) : null}

        {isAdminPreview ? (
          <Alert variant="info" title="Admin preview">
            Admin preview — you are not capturing this room.
          </Alert>
        ) : null}

        {canEdit && session !== null && space !== null ? (
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-cc-charcoal)]/55">
              Asset register
            </span>
            <button
              type="button"
              onClick={() => setAddAssetOpen(true)}
              data-testid="add-asset-button"
              className="inline-flex min-h-9 items-center gap-1 rounded-full px-1 text-xs font-semibold text-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream-2)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
            >
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
              Add asset
            </button>
          </div>
        ) : null}

        {sessionIdFromNav === null ? (
          <Alert variant="info" title="Open this room from the building page">
            Open a building from the site list and tap a room to capture. Room
            capture needs the session context; direct deep links will be
            supported alongside the Phase-3 sync layer.
          </Alert>
        ) : loadError !== null ? (
          <Alert variant="error" title="Could not load this room">
            {loadError}
          </Alert>
        ) : session === null || authPending ? (
          <Spinner label="Loading room capture…" size="md" />
        ) : space === null ? (
          <Alert variant="error" title="Room not found">
            This room is no longer part of the session. It may have been
            removed — return to the building to refresh the list.
          </Alert>
        ) : systemBuckets.length === 0 ? (
          <EmptyState
            title="No template items for this room type yet."
            description="Tap + Add Asset to start capturing."
            action={
              canEdit ? (
                <Button
                  variant="primary"
                  onClick={() => setAddAssetOpen(true)}
                  data-testid="add-asset-button-empty"
                >
                  + Add Asset
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            {systemBuckets.map((bucket) => (
              <SystemGroup
                key={bucket.systemId}
                systemName={bucket.systemName}
                items={bucket.items}
                onUpdate={handleUpdate}
                onRemove={canEdit ? handleRequestRemove : undefined}
                variant="compact"
                storageKey={`citycare:capture-collapse:${spaceId}:${bucket.systemId}`}
              />
            ))}
          </div>
        )}

        {canEdit && space !== null ? (
          <StickyBottomBar>
            <Button
              variant="primary"
              onClick={handleDone}
              aria-label="Done with room"
              className="flex-1 min-h-12 justify-center gap-1.5 rounded-2xl bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-lime)] hover:bg-[color:var(--color-cc-forest)]/90"
            >
              Done
              <Check aria-hidden="true" className="h-4 w-4" />
            </Button>
          </StickyBottomBar>
        ) : null}

        {canEdit && session !== null && space !== null ? (
          <AddAssetDialog
            open={isAddAssetOpen}
            onClose={() => setAddAssetOpen(false)}
            sessionId={session.session.id}
            spaceId={space.id}
            spaceName={space.displayName}
            excludedAssetTypeIds={excludedAssetTypeIds}
            captureClient={client}
            onSelectAssetType={handleSelectAssetType}
            renderCreateVariantForm={(props) => (
              <CreateVariantForm
                client={client}
                subSystemId={props.subSystemId}
                subSystemName={props.subSystemName}
                systemName={props.systemName}
                initialName={props.initialName}
                onCreated={props.onCreated}
                onCancel={props.onCancel}
              />
            )}
          />
        ) : null}

        <RemoveAssetConfirmDialog
          open={pendingConfirm !== null}
          assetName={pendingConfirm?.assetName ?? ''}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
        />
      </section>
    </CaptureLayout>
  )
}
